import type { ConstantsAuditLogStruct } from "@gosaas/types";
import { hasPermission, type Permission, type Role } from "@gosaas/types/rbac";
import {
	IconActivity,
	IconClock,
	IconLock,
	IconShield,
	IconUsers,
} from "@tabler/icons-react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "sonner";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useTeam } from "@/components/providers/team";
import { useFetchTeamAuditLogs } from "@/components/queries/security";
import { auditLogColumns } from "@/components/tables/columns";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";

export const Route = createFileRoute("/_authorized/team/security")({
	component: Security,
});

export function computeSecurityMetrics(
	auditLogs: Array<{ action?: string; ip_address?: string }> | null | undefined,
) {
	const logs = auditLogs ?? [];
	return {
		totalLogs: logs.length,
		loginAttempts: logs.filter((log) => log.action?.includes("TEAM_CREATED"))
			.length,
		teamInvites: logs.filter((log) =>
			log.action?.includes("TEAM_MEMBER_INVITED"),
		).length,
		uniqueIPs: new Set(logs.map((log) => log.ip_address)).size,
	};
}

export function auditLogsForTable(
	auditLogs: ConstantsAuditLogStruct[] | null | undefined,
): ConstantsAuditLogStruct[] {
	return auditLogs ?? [];
}

export function Security() {
	const { teamID, teams } = useTeam();
	const router = useRouter();
	const { auditLogs, isLoadingAuditLogs } = useFetchTeamAuditLogs(
		teamID as string,
	);

	const team = teams.find((t) => t.id === teamID);
	const canReadSecurity = hasPermission(
		team?.role as Role,
		"security:read" as Permission,
	);

	useEffect(() => {
		if (canReadSecurity === false) {
			toast.warning("You do not have permission to manage security", {
				description: "Please contact support if you believe this is an error.",
			});
			router.navigate({ to: "/403" });
		}
	}, [canReadSecurity, router]);

	if (isLoadingAuditLogs) {
		return <Loader />;
	}
	if (!canReadSecurity) {
		return null;
	}

	const { totalLogs, loginAttempts, teamInvites, uniqueIPs } =
		computeSecurityMetrics(auditLogs);

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Team", href: null },
					{ title: "Security", href: "/team/security" },
				]}
				title="Security"
				description="Manage your team security and audit logs"
				icon={<IconLock className="size-6 text-primary" aria-hidden />}
			/>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
				<Card className="shadow-sm">
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-primary/10 rounded-lg">
								<IconActivity className="h-5 w-5 text-primary" />
							</div>
							<div>
								<p className="text-2xl font-bold">{totalLogs}</p>
								<p className="text-sm text-muted-foreground">Total Events</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card className="shadow-sm">
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-green-500/10 rounded-lg">
								<IconShield className="h-5 w-5 text-green-600" />
							</div>
							<div>
								<p className="text-2xl font-bold">{loginAttempts}</p>
								<p className="text-sm text-muted-foreground">Login Events</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card className="shadow-sm">
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-blue-500/10 rounded-lg">
								<IconUsers className="h-5 w-5 text-blue-600" />
							</div>
							<div>
								<p className="text-2xl font-bold">{teamInvites}</p>
								<p className="text-sm text-muted-foreground">Team Actions</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card className="shadow-sm">
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-orange-500/10 rounded-lg">
								<IconClock className="h-5 w-5 text-orange-600" />
							</div>
							<div>
								<p className="text-2xl font-bold">{uniqueIPs}</p>
								<p className="text-sm text-muted-foreground">Unique IPs</p>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-purple-500/10 rounded-lg">
							<IconShield className="h-5 w-5 text-purple-500" />
						</div>
						<div>
							<CardTitle className="text-xl">Audit Log History</CardTitle>
							<CardDescription className="text-pretty">
								Comprehensive log of all security events and user activities.
								Click on user agent entries to view detailed browser
								information.
							</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					<DataTable
						columns={auditLogColumns}
						data={auditLogsForTable(auditLogs)}
						usePagination={true}
						pageSize={5}
						loading={false}
						filterColumn="action"
					/>
				</CardContent>
			</Card>
		</PageLayout>
	);
}
